import { useState, useRef } from 'react';
import { FloorPlanFixture as FixtureType, FIXTURE_TYPES } from '@/types/fixture';
import { cn } from '@/lib/utils';
import { 
  Square, 
  Minus, 
  Wine, 
  Store, 
  ArrowUpDown, 
  DoorOpen, 
  TreeDeciduous, 
  Circle,
  GripVertical,
  Trash2,
  Palette,
  ChevronUp,
  ChevronDown,
  RotateCw,
  RotateCcw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface FloorPlanFixtureProps {
  fixture: FixtureType;
  cellSize: number;
  isEditMode: boolean;
  isSelected?: boolean;
  onSelect?: (fixtureId: string | null) => void;
  onPositionChange: (fixture: FixtureType) => void;
  onDelete: (id: string) => void;
  gridCols: number;
  gridRows: number;
}

const FIXTURE_ICONS = {
  rectangle: Square,
  circle: Circle,
  wall: Minus,
  bar: Wine,
  counter: Store,
  stairs: ArrowUpDown,
  door: DoorOpen,
  plant: TreeDeciduous,
};

export function FloorPlanFixture({
  fixture,
  cellSize,
  isEditMode,
  isSelected = false,
  onSelect,
  onPositionChange,
  onDelete,
  gridCols,
  gridRows,
}: FloorPlanFixtureProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const dragStartRef = useRef<{ x: number; y: number; startX: number; startY: number } | null>(null);
  const fixtureRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleMouseEnter = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setIsHovering(true);
  };

  const handleMouseLeave = () => {
    // Delay hiding to allow moving to the control bar
    hoverTimeoutRef.current = setTimeout(() => {
      setIsHovering(false);
    }, 150);
  };

  const Icon = FIXTURE_ICONS[fixture.type] || Square;
  const config = FIXTURE_TYPES.find(t => t.type === fixture.type);

  // Show controls when hovering OR selected (for mobile/tablet support)
  const showControls = isEditMode && (isHovering || isSelected) && !isDragging;

  // Calculate if toolbar should appear below (to avoid clipping at top edge)
  const toolbarOnBottom = fixture.y < 1;

  const handleClick = (e: React.MouseEvent) => {
    if (!isEditMode) return;
    e.stopPropagation();
    onSelect?.(isSelected ? null : fixture.id);
  };

  // Shared drag logic for both mouse and touch
  const startDrag = (clientX: number, clientY: number) => {
    setIsDragging(true);
    dragStartRef.current = {
      x: clientX,
      y: clientY,
      startX: fixture.x,
      startY: fixture.y,
    };
  };

  const moveDrag = (clientX: number, clientY: number) => {
    if (!dragStartRef.current) return;
    
    const deltaX = clientX - dragStartRef.current.x;
    const deltaY = clientY - dragStartRef.current.y;
    
    const newX = Math.max(0, Math.min(
      gridCols - fixture.width,
      dragStartRef.current.startX + Math.round(deltaX / cellSize)
    ));
    const newY = Math.max(0, Math.min(
      gridRows - fixture.height,
      dragStartRef.current.startY + Math.round(deltaY / cellSize)
    ));
    
    if (newX !== fixture.x || newY !== fixture.y) {
      onPositionChange({ ...fixture, x: newX, y: newY });
    }
  };

  const endDrag = () => {
    setIsDragging(false);
    dragStartRef.current = null;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!isEditMode) return;
    
    // Only start dragging if clicking on the fixture itself, not on controls
    const target = e.target as HTMLElement;
    if (target.closest('[data-controls]')) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    startDrag(e.clientX, e.clientY);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      moveDrag(moveEvent.clientX, moveEvent.clientY);
    };

    const handleMouseUp = () => {
      endDrag();
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Touch event handlers for mobile support
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isEditMode) return;
    
    const target = e.target as HTMLElement;
    if (target.closest('[data-controls]')) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const touch = e.touches[0];
    startDrag(touch.clientX, touch.clientY);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!dragStartRef.current || !isEditMode) return;
    const touch = e.touches[0];
    moveDrag(touch.clientX, touch.clientY);
  };

  const handleTouchEnd = () => {
    endDrag();
  };

  const handleSizeChange = (dimension: 'width' | 'height', delta: number) => {
    const newValue = Math.max(1, fixture[dimension] + delta);
    onPositionChange({ ...fixture, [dimension]: newValue });
  };

  const handleRotate = (delta: number) => {
    const newRotation = (fixture.rotation + delta + 360) % 360;
    onPositionChange({ ...fixture, rotation: newRotation });
  };

  const handleColorChange = (color: string) => {
    onPositionChange({ ...fixture, color });
  };

  // Get visual styles based on fixture type
  const getFixtureStyles = () => {
    const baseStyle = {
      backgroundColor: fixture.color,
      transform: `rotate(${fixture.rotation}deg)`,
    };

    switch (fixture.type) {
      case 'circle':
        return {
          ...baseStyle,
          borderRadius: '50%',
        };
      case 'wall':
        return {
          ...baseStyle,
          borderRadius: '2px',
        };
      case 'door':
        return {
          ...baseStyle,
          borderRadius: '4px',
          borderBottom: '4px solid rgba(255,255,255,0.3)',
        };
      case 'stairs':
        return {
          ...baseStyle,
          backgroundImage: `repeating-linear-gradient(
            90deg,
            transparent,
            transparent 4px,
            rgba(255,255,255,0.2) 4px,
            rgba(255,255,255,0.2) 8px
          )`,
        };
      case 'bar':
      case 'counter':
        return {
          ...baseStyle,
          borderRadius: '4px',
          boxShadow: 'inset 0 -3px 0 rgba(0,0,0,0.2)',
        };
      case 'plant':
        return {
          ...baseStyle,
          borderRadius: fixture.width === fixture.height ? '50%' : '8px',
        };
      default:
        return {
          ...baseStyle,
          borderRadius: '4px',
        };
    }
  };

  return (
    <div
      ref={fixtureRef}
      className={cn(
        "absolute flex items-center justify-center transition-shadow select-none z-10",
        isEditMode && "cursor-move",
        isDragging && "!z-50 shadow-xl",
        isEditMode && !isDragging && "hover:shadow-lg",
        isSelected && isEditMode && "ring-2 ring-primary ring-offset-2 !z-30"
      )}
      style={{
        left: `${fixture.x * cellSize}px`,
        top: `${fixture.y * cellSize}px`,
        width: `${fixture.width * cellSize}px`,
        height: `${fixture.height * cellSize}px`,
        ...getFixtureStyles(),
      }}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Content */}
      <div className="flex flex-col items-center justify-center text-white/80 pointer-events-none">
        <Icon className="w-5 h-5" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }} />
        {fixture.name && cellSize >= 40 && (
          <span 
            className="text-[10px] font-medium text-center px-1 truncate max-w-full"
            style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}
          >
            {fixture.name}
          </span>
        )}
      </div>

      {/* Edit mode controls - positioned dynamically to avoid clipping */}
      {showControls && (
        <div
          data-controls
          className={cn(
            "absolute left-1/2 -translate-x-1/2 flex items-center gap-1 bg-background border rounded-lg shadow-lg p-1 z-50",
            toolbarOnBottom ? "top-full mt-2" : "-top-10"
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Drag handle */}
          <div className="flex items-center justify-center w-6 h-6 text-muted-foreground cursor-grab">
            <GripVertical className="w-4 h-4" />
          </div>

          {/* Width controls */}
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleSizeChange('width', -1)}
              disabled={fixture.width <= 1}
              title="Réduire la largeur"
            >
              <ChevronDown className="w-3 h-3" />
            </Button>
            <span className="text-xs w-4 text-center" title="Largeur">{fixture.width}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleSizeChange('width', 1)}
              title="Augmenter la largeur"
            >
              <ChevronUp className="w-3 h-3" />
            </Button>
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Height controls */}
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleSizeChange('height', -1)}
              disabled={fixture.height <= 1}
              title="Réduire la hauteur"
            >
              <ChevronDown className="w-3 h-3" />
            </Button>
            <span className="text-xs w-4 text-center" title="Hauteur">{fixture.height}</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleSizeChange('height', 1)}
              title="Augmenter la hauteur"
            >
              <ChevronUp className="w-3 h-3" />
            </Button>
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Rotation controls */}
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleRotate(-90)}
              title="Rotation -90°"
            >
              <RotateCcw className="w-3 h-3" />
            </Button>
            <span className="text-xs w-6 text-center" title="Rotation">{fixture.rotation}°</span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => handleRotate(90)}
              title="Rotation +90°"
            >
              <RotateCw className="w-3 h-3" />
            </Button>
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Color picker */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="h-6 w-6" title="Couleur">
                <Palette className="w-3 h-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-3" align="center">
              <div className="space-y-2">
                <Label className="text-xs">Couleur</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="color"
                    value={fixture.color}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-10 h-8 p-0 border-0 cursor-pointer"
                  />
                  <Input
                    type="text"
                    value={fixture.color}
                    onChange={(e) => handleColorChange(e.target.value)}
                    className="w-24 h-8 text-xs"
                  />
                </div>
                <div className="flex flex-wrap gap-1">
                  {['#374151', '#6b7280', '#92400e', '#78716c', '#64748b', '#a16207', '#15803d', '#1e40af'].map(color => (
                    <button
                      key={color}
                      className="w-6 h-6 rounded border-2 border-transparent hover:border-foreground/50 transition-colors"
                      style={{ backgroundColor: color }}
                      onClick={() => handleColorChange(color)}
                    />
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <div className="w-px h-4 bg-border" />

          {/* Delete button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={() => onDelete(fixture.id)}
            title="Supprimer"
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      )}
    </div>
  );
}
